import { ReactElement, useEffect, useState } from 'react';
import { Select } from '.';
import classNames from '../utilities/class-names';
import './DurationInput.scss';

type DurationUnit = 'never' | 'minutes' | 'hours' | 'days';

const UNIT_SECONDS: Record<Exclude<DurationUnit, 'never'>, number> = {
  minutes: 60,
  hours: 60 * 60,
  days: 24 * 60 * 60,
};

type DurationInputProps = {
  id?: string;
  className?: string;

  /**
   * The duration in seconds, or null for "never" (if allowNever is set)
   */
  value: number | null;
  allowNever?: boolean;
  disabled?: boolean;
  onChange?: (value: number | null) => void;
};

/**
 * Split a number of seconds into the largest unit that divides it exactly
 */
function splitDuration(
  value: number | null,
  allowNever: boolean
): [number, DurationUnit] {
  if (value === null) {
    return allowNever ? [1, 'never'] : [1, 'hours'];
  }

  for (const unit of ['days', 'hours', 'minutes'] as const) {
    if (value % UNIT_SECONDS[unit] === 0) {
      return [value / UNIT_SECONDS[unit], unit];
    }
  }

  return [Math.max(1, Math.round(value / 60)), 'minutes'];
}

export function DurationInput({
  id,
  className,
  value,
  allowNever,
  disabled,
  onChange,
}: DurationInputProps): ReactElement {
  const [[amount, unit], setDuration] = useState(() =>
    splitDuration(value, !!allowNever)
  );

  useEffect(() => {
    setDuration(current => {
      // Don't disturb what's being typed if it already represents this value
      const currentValue =
        current[1] === 'never' ? null : current[0] * UNIT_SECONDS[current[1]];

      return currentValue === value
        ? current
        : splitDuration(value, !!allowNever);
    });
  }, [value, allowNever]);

  const handleChange = (newAmount: number, newUnit: DurationUnit) => {
    setDuration([newAmount, newUnit]);

    if (newUnit === 'never') {
      onChange?.(null);
    } else if (Number.isFinite(newAmount) && newAmount > 0) {
      onChange?.(Math.round(newAmount * UNIT_SECONDS[newUnit]));
    }
  };

  const unitOptions = [
    ...(allowNever ? [{ label: 'Never expires', value: 'never' }] : []),
    { label: 'Minutes', value: 'minutes' },
    { label: 'Hours', value: 'hours' },
    { label: 'Days', value: 'days' },
  ];

  return (
    <div className={classNames('duration-input', className)}>
      {unit !== 'never' && (
        <input
          type="number"
          id={id}
          min={1}
          step={1}
          value={Number.isFinite(amount) ? amount : ''}
          disabled={disabled}
          onChange={e => handleChange(parseInt(e.target.value, 10), unit)}
        />
      )}
      <Select
        inputId={unit === 'never' ? id : undefined}
        options={unitOptions}
        value={unitOptions.find(option => option.value === unit)}
        disabled={disabled}
        onChange={selected =>
          selected &&
          handleChange(amount, (selected as any).value as DurationUnit)
        }
      />
    </div>
  );
}
